import { PlusOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { ReportIllustration } from "./shell/ReportsIntro.tsx"

const { Text, Title } = Typography

/**
 * - `none`: the user can open no report at all — the first-use invitation.
 * - `folder`: the chosen folder (or Unfiled) holds nothing yet.
 * - `favorites` / `recent` / `shared`: that quick view is empty.
 * - `search`: reports exist here, but none match the search.
 */
export type ReportsEmptyKind =
  | "none"
  | "folder"
  | "favorites"
  | "recent"
  | "shared"
  | "search"

interface ReportsEmptyProps {
  kind: ReportsEmptyKind
  onCreate: () => void
  onClearSearch: () => void
  /** The button that opens New Report, so focus can come back to it. */
  createButtonRef?: React.Ref<HTMLButtonElement>
}

export function ReportsEmpty({
  kind,
  onCreate,
  onClearSearch,
  createButtonRef
}: ReportsEmptyProps) {
  const { t } = useTranslation()

  if (kind === "none")
    return (
      <Flex
        vertical
        align="center"
        gap="small"
        className="mx-auto max-w-[520px] px-6 py-12 text-center"
      >
        <div className="h-[110px]">
          <ReportIllustration />
        </div>
        <Title level={4} className="m-0 mt-2 text-center">
          {t("reports.empty.none.title")}
        </Title>
        <Text type="secondary">{t("reports.empty.none.subtitle")}</Text>
        <Button
          ref={createButtonRef}
          type="primary"
          size="large"
          icon={<PlusOutlined aria-hidden />}
          onClick={onCreate}
          className="mt-3"
        >
          {t("reports.empty.createFirst")}
        </Button>
        <Text type="secondary" className="text-xs">
          {t("reports.empty.none.footnote")}
        </Text>
      </Flex>
    )

  const action =
    kind === "search" ? (
      <Button onClick={onClearSearch}>{t("reports.empty.clearFilters")}</Button>
    ) : kind === "folder" ? (
      <Button
        ref={createButtonRef}
        type="primary"
        icon={<PlusOutlined aria-hidden />}
        onClick={onCreate}
      >
        {t("reports.empty.createHere")}
      </Button>
    ) : null

  return (
    <Empty
      className="py-10"
      description={
        <Flex vertical align="center" gap="small" className="text-center">
          <Text strong>{t(`reports.empty.${kind}.title`)}</Text>
          <Text type="secondary">{t(`reports.empty.${kind}.subtitle`)}</Text>
          {action && <div className="pt-2">{action}</div>}
        </Flex>
      }
    />
  )
}
